import { find } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AdditionalOrderColumn,
  DisputeStatusDetails,
  FilterDefinitionType,
  OrderDisputeType,
  OrdersTab,
} from 'enums';
import { Orders } from 'features/orders';
import {
  useWhiteTriangle,
  useCurrencies,
  useOrderQueryFilters,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { orderUtils, formUtils, requisitesUtils } from 'utils';

export const TraderDisputeOrders: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const { isWhiteTriangle, requisitesId } = useWhiteTriangle();
  useOrderQueryFilters({
    ...(isWhiteTriangle && { requisitesId }),
  });

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('dispute'),
        name: 'statusDetails',
        type: FilterDefinitionType.Enum,
        enum: Object.values(DisputeStatusDetails),
        getDisplayName: (value: DisputeStatusDetails) =>
          orderUtils.getStatusDetailsLabel(value),
      },
      {
        label: tFilters('dispute_type'),
        name: 'disputeType',
        type: FilterDefinitionType.Enum,
        enum: Object.values(OrderDisputeType),
        getDisplayName: (value: OrderDisputeType) =>
          orderUtils.getDisputeTypeLabel(value),
      },
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
        hidden: isWhiteTriangle,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
        hidden: isWhiteTriangle,
      },
      ...(!isWhiteTriangle
        ? requisitesUtils.getRequisitesFieldsFilters<OrderFilters>()
        : []),
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      tFilters,
      isWhiteTriangle,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.Dispute}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.CustomerPayment,
        AdditionalOrderColumn.TraderCard,
        AdditionalOrderColumn.ResolveDispute,
      ]}
    />
  );
};
